/**
 * UserPromptSubmit Hook Handler — navigation-index summary injection.
 *
 * Executed after prompt submission, before Claude processes it. Provides a
 * compact navigation-index summary so the LLM can pick candidates and delegate
 * deep exploration to `analyzer.getContextMap` (Level 2).
 *
 * ContextPack is NOT pre-injected by default (#206 Phase D).
 */

import { renderNavigationIndexPrompt } from "../task-runner.js";
import {
  ensureNavigationIndex,
  type EnsureNavigationIndexOptions,
} from "./ensure-navigation-index.js";
import {
  listArtifacts,
} from "./artifact-resolver.js";
import {
  buildContextResponse,
  type HookOutput,
  type UserPromptSubmitInput,
} from "./hook-protocol.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UserPromptSubmitHandlerConfig {
  /** Absolute path to the project root. */
  projectRoot: string;
  /** Absolute path to .claude/cache/ directory. */
  cacheDir: string;
  /** Source files for cache invalidation. */
  sourceFiles: string[];
  /** Options for buildNavigationContext(). */
  navigationOptions: EnsureNavigationIndexOptions["navigationOptions"];
}

// ---------------------------------------------------------------------------
// Handler
// ---------------------------------------------------------------------------

/**
 * Create a UserPromptSubmit hook handler.
 */
export function createUserPromptSubmitHandler(
  config: UserPromptSubmitHandlerConfig,
) {
  return async function handleUserPromptSubmit(
    input: UserPromptSubmitInput,
  ): Promise<HookOutput> {
    const prompt = input.prompt ?? "";

    const navResult = await ensureNavigationIndex({
      cacheDir: config.cacheDir,
      sourceFiles: config.sourceFiles,
      navigationOptions: config.navigationOptions,
    });
    const navigationIndex = navResult.navigationIndex;
    const navigationSummary = renderNavigationIndexPrompt(navigationIndex);

    const sections: string[] = [
      "## Project Navigation Index",
      navigationSummary,
      "",
      "> Use `analyzer.getContextMap` with candidate paths/artifacts from the index",
      "> when you need structured dependency and read-target guidance.",
    ];

    const candidates = extractCandidateArtifacts(prompt, navigationIndex);
    if (candidates.length > 0) {
      const candidateLines = candidates.map((c) => {
        const props = c.artifact.properties ?? {};
        const tags = [props.type, props.authority, props.manual_edit ? `edit:${props.manual_edit}` : ""]
          .filter(Boolean)
          .join(", ");
        return `- ${c.artifactId} [${tags}]`;
      });
      sections.push(
        "## Candidate Artifacts (from prompt keywords)\n" + candidateLines.join("\n"),
      );
    }

    const additionalContext = sections.join("\n");
    return buildContextResponse("UserPromptSubmit", additionalContext);
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface CandidateArtifact {
  artifactId: string;
  artifact: ReturnType<typeof listArtifacts>[number]["artifact"];
}

function extractCandidateArtifacts(
  prompt: string,
  navigationIndex: Record<string, unknown>,
): CandidateArtifact[] {
  if (!prompt.trim()) return [];

  const artifacts = listArtifacts(navigationIndex);
  const lowerPrompt = prompt.toLowerCase();
  const matches: CandidateArtifact[] = [];
  const seen = new Set<string>();

  for (const { id, artifact } of artifacts) {
    if (lowerPrompt.includes(id.toLowerCase()) && !seen.has(id)) {
      seen.add(id);
      matches.push({ artifactId: id, artifact });
      continue;
    }

    const patterns = artifact.files?.path_patterns ?? [];
    for (const pattern of patterns) {
      if (typeof pattern !== "string") continue;
      const literal = pattern.replace(/[*?]/g, "").replace(/\{.*\}/g, "");
      if (literal.length > 3 && lowerPrompt.includes(literal.toLowerCase()) && !seen.has(id)) {
        seen.add(id);
        matches.push({ artifactId: id, artifact });
        break;
      }
    }
  }

  return matches;
}
